import { ResourceUsageContent } from "@/components/environments/EnvironmentPerformance";
import {
    getHealthLevel,
    healthLevelLabel,
    healthLevelTooltip,
    healthPillVariant,
} from "@/components/environments/resource-usage-health";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { Pill } from "@/components/podkit/status/Pill";
import { Tooltip } from "@/components/Tooltip";
import { useVMLiveUsage } from "@/hooks/use-vm-live-usage";
import { TrackLocations } from "@/hooks/use-segment";
import { useEnvironment } from "@/queries/environment-queries";
import type { FC } from "react";

type ResourceUsageDialogProps = {
    environmentId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export const ResourceUsageDialog: FC<ResourceUsageDialogProps> = ({ environmentId, open, onOpenChange }) => {
    const { data, diskIORate, error, isLoading, lastUpdated, refetch } = useVMLiveUsage(environmentId, open);
    const { data: environment } = useEnvironment(environmentId);
    const projectId = environment?.metadata?.projectId;
    const healthLevel = getHealthLevel(data);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-track-location={TrackLocations.ResourceUsageModal}>
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle>Resource Usage</DialogTitle>
                        <Tooltip content={healthLevelTooltip(healthLevel)} usePortal sideOffset={4} className="!m-0">
                            <span>
                                <Pill variant={healthPillVariant(healthLevel)} size="md">
                                    {healthLevelLabel(healthLevel)}
                                </Pill>
                            </span>
                        </Tooltip>
                    </div>
                    <DialogDescription>CPU, memory, disk, and load for this environment.</DialogDescription>
                </DialogHeader>
                <ResourceUsageContent
                    data={data}
                    diskIORate={diskIORate}
                    error={error}
                    isLoading={isLoading}
                    lastUpdated={lastUpdated}
                    onRefresh={refetch}
                    projectId={projectId}
                />
            </DialogContent>
        </Dialog>
    );
};
